import { Backdrop, CircularProgress, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
  getSummaryByOU,
  getSummaryByOUFilters,
  getSummaryByOUPeriods,
  OUColumn,
  SummaryByOUFiltersResponse,
  SummaryByOUResponse,
} from '../../api'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'
import styles from './AdoptionDashboard.module.css'
import SummaryByOUFilterBar from './SummaryByOUFilterBar'
import SummaryByOUMatrixTable from './SummaryByOUMatrixTable'
import { exportSummaryByOUExcel } from './exportSummaryByOU'
import { launchedSortValue, parsePeriod, SortColumn, SortDirection } from './summaryByOUConstants'

const SummaryByOUTab = () => {
  const appStateContext = useContext(AppStateContext)

  const [summaryByOUData, setSummaryByOUData] = useState<SummaryByOUResponse | undefined>(undefined)
  const [summaryByOUFilters, setSummaryByOUFilters] = useState<SummaryByOUFiltersResponse | undefined>(undefined)
  const [summaryByOUPeriods, setSummaryByOUPeriods] = useState<{ value: string; label: string }[] | undefined>(
    undefined,
  )
  const [selectedSummaryPeriod, setSelectedSummaryPeriod] = useState<string>('')
  const [selectedSummaryTitle, setSelectedSummaryTitle] = useState<string>('')
  const [selectedSummaryBPD, setSelectedSummaryBPD] = useState<string>('')
  const [summaryByOUFetchError, setSummaryByOUFetchError] = useState<boolean>(false)
  const [sortColumn, setSortColumn] = useState<SortColumn>('launched_in')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false)
  const [isExportBackdropOpen, setIsExportBackdropOpen] = useState<boolean>(false)

  const { quarter: selectedQuarter, year: selectedYear } = useMemo(
    () =>
      selectedSummaryPeriod ? parsePeriod(selectedSummaryPeriod) : { quarter: '', year: new Date().getFullYear() },
    [selectedSummaryPeriod],
  )

  useEffect(() => {
    getSummaryByOUPeriods().then(data => {
      if (data && data.length > 0) {
        const periodOptions = data.map(p => ({
          value: `${p.quarter} ${p.year}`,
          label: `${p.quarter} ${p.year}`,
        }))
        setSummaryByOUPeriods(periodOptions)

        // Auto-select YTD for the latest year available
        const latestYear = Math.max(...data.map(p => p.year))
        const ytdOption = periodOptions.find(p => p.value === `YTD ${latestYear}`)
        const defaultPeriod = ytdOption ? ytdOption.value : periodOptions[0].value
        setSelectedSummaryPeriod(defaultPeriod)
        const { quarter, year } = parsePeriod(defaultPeriod)
        fetchSummaryByOUData(year, quarter)
        fetchSummaryByOUFiltersData(year, quarter)
      }
    })
  }, [])

  const fetchSummaryByOUData = async (year: number, quarter: string, title?: string, bpd?: string) => {
    try {
      const data = await getSummaryByOU(year, quarter, title || undefined, bpd || undefined)
      if (data) {
        setSummaryByOUFetchError(false)
        setSummaryByOUData(data)
      } else {
        // null means a network / server error (non-200 response)
        setSummaryByOUFetchError(true)
        setSummaryByOUData(undefined)
      }
    } catch {
      setSummaryByOUFetchError(true)
      setSummaryByOUData(undefined)
    }
  }

  const fetchSummaryByOUFiltersData = async (year: number, quarter: string) => {
    try {
      const filters = await getSummaryByOUFilters(year, quarter)
      if (filters) {
        setSummaryByOUFilters(filters)
      }
    } catch {
      setSummaryByOUFetchError(true)
    }
  }

  const handleSummaryPeriodChange = async (period: string) => {
    setSelectedSummaryPeriod(period)
    setSelectedSummaryTitle('')
    setSelectedSummaryBPD('')
    const { quarter, year } = parsePeriod(period)
    await fetchSummaryByOUFiltersData(year, quarter)
    await fetchSummaryByOUData(year, quarter)
  }

  const handleSummaryTitleChange = async (title: string) => {
    setSelectedSummaryTitle(title)
    await fetchSummaryByOUData(selectedYear, selectedQuarter, title, selectedSummaryBPD)
  }

  const handleSummaryBPDChange = async (bpd: string) => {
    setSelectedSummaryBPD(bpd)
    await fetchSummaryByOUData(selectedYear, selectedQuarter, selectedSummaryTitle, bpd)
  }

  const handleResetFilters = async () => {
    setSelectedSummaryTitle('')
    setSelectedSummaryBPD('')
    await fetchSummaryByOUData(selectedYear, selectedQuarter)
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const ouColumns: OUColumn[] = useMemo(() => summaryByOUFilters?.ou_columns ?? [], [summaryByOUFilters])

  const sortedRows = useMemo(() => {
    if (!summaryByOUData) return []
    const rows = [...summaryByOUData.rows]
    const dir = sortDirection === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      switch (sortColumn) {
        case 'title':
          return dir * a.title.localeCompare(b.title)
        case 'decoded_learning':
          return dir * a.decoded_learning.localeCompare(b.decoded_learning)
        case 'launched_in': {
          const aVal = launchedSortValue(a.launched_quarter, a.launched_year)
          const bVal = launchedSortValue(b.launched_quarter, b.launched_year)
          return dir * (aVal - bVal)
        }
        default:
          return 0
      }
    })
    return rows
  }, [summaryByOUData, sortColumn, sortDirection])

  const hasData = !!summaryByOUData && summaryByOUData.rows.length > 0

  const handleExportExcel = async () => {
    setIsExportDialogOpen(false)
    setIsExportBackdropOpen(true)

    if (!summaryByOUData || summaryByOUData.rows.length === 0) {
      setIsExportBackdropOpen(false)
      return
    }

    try {
      await exportSummaryByOUExcel(summaryByOUData, ouColumns, selectedYear, selectedQuarter)

      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'File downloaded successfully.', severity: 'success' },
      })
    } catch {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Download failed. Try again later.', severity: 'warning' },
      })
    } finally {
      setIsExportBackdropOpen(false)
    }
  }

  return (
    <>
      <SummaryByOUFilterBar
        periods={summaryByOUPeriods}
        selectedPeriod={selectedSummaryPeriod}
        selectedTitle={selectedSummaryTitle}
        selectedBPD={selectedSummaryBPD}
        selectedYear={selectedYear}
        titles={summaryByOUFilters?.titles}
        decodedLearnings={summaryByOUFilters?.decoded_learnings}
        exportDisabled={!hasData}
        onPeriodChange={handleSummaryPeriodChange}
        onTitleChange={handleSummaryTitleChange}
        onBPDChange={handleSummaryBPDChange}
        onResetFilters={handleResetFilters}
        onExport={() => setIsExportDialogOpen(true)}
      />

      <SummaryByOUMatrixTable
        rows={sortedRows}
        ouColumns={ouColumns}
        fetchError={summaryByOUFetchError}
        hasData={hasData}
        selectedYear={selectedYear}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      <Backdrop
        sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isExportBackdropOpen}>
        <div className={styles.backdropMessageContainer}>
          <CircularProgress color="inherit" />
          <p className={styles.backdropMessage}>Exporting file...</p>
        </div>
      </Backdrop>
      <CustomDialog
        title="Export Excel File"
        open={isExportDialogOpen}
        setClose={() => setIsExportDialogOpen(false)}
        primaryAction={() => handleExportExcel()}
        secondaryAction={() => setIsExportDialogOpen(false)}
        primaryActionButtonText="Export"
        secondaryActionButtonText="Cancel">
        <Typography className={styles.exportDialogText}>Are you sure that you want to export this file?</Typography>
      </CustomDialog>
    </>
  )
}

export default SummaryByOUTab
